import { Button, notify, useConfirmModal } from '@affine/component';
import {
  InviteTeamMemberModal,
  type InviteTeamMemberModalProps,
  MemberLimitModal,
} from '@affine/component/member-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { Upload } from '@affine/core/components/pure/file-upload';
import {
  SeatAddonService,
  SubscriptionService,
  WorkspaceSubscriptionService,
} from '@affine/core/modules/cloud';
import {
  WorkspaceMembersService,
  WorkspacePermissionService,
} from '@affine/core/modules/permissions';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { WorkspaceShareSettingService } from '@affine/core/modules/share-setting';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { copyTextToClipboard } from '@affine/core/utils/clipboard';
import { emailRegex } from '@affine/core/utils/email-regex';
import { UserFriendlyError } from '@affine/error';
import {
  Permission,
  type WorkspaceInviteLinkExpireTime,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { ExportIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useDebouncedValue } from 'foxact/use-debounced-value';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import type { SettingState } from '../../types';
import { SettingsEntityMessage } from '../shared/settings-entity';
import {
  SettingsEntityPage,
  SettingsSearchableEntityPage,
} from '../shared/settings-entity-page';
import { MemberList, MemberListFallback } from './member-list';
import { SeatQuotaBanner } from './seat-quota-banner';
import { SeatUpsellDialog } from './seat-upsell-dialog';
import * as styles from './styles.css';

const parseCSV = async (blob: Blob): Promise<string[]> => {
  try {
    const textContent = await blob.text();
    const emails = textContent
      .split('\n')
      .map(email => email.trim())
      .filter(email => email.length > 0 && emailRegex.test(email));

    return emails;
  } catch (error) {
    console.error('Error parsing CSV:', error);
    throw new Error('Failed to parse CSV');
  }
};

type FailedInvite = {
  email: string;
  message?: string;
};

const inviteFailureMessage = (error: unknown): string | undefined => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }
  return undefined;
};

export const CloudWorkspaceMembersPanel = ({
  onChangeSettingState,
  isTeam,
}: {
  onChangeSettingState: (settingState: SettingState) => void;
  isTeam?: boolean;
}) => {
  const workspaceShareSettingService = useService(WorkspaceShareSettingService);
  const workspace = useService(WorkspaceService).workspace;
  const backendAvailable = useBackendAvailable();
  const subscription = useService(WorkspaceSubscriptionService).subscription;
  const workspaceSubscription = useLiveData(subscription.subscription$);
  const inviteLink = useLiveData(
    workspaceShareSettingService.sharePreview.inviteLink$
  );
  const membersService = useService(WorkspaceMembersService);
  const permissionService = useService(WorkspacePermissionService);
  const memberCount = useLiveData(membersService.members.memberCount$);
  const pageNum = useLiveData(membersService.members.pageNum$);
  const searchQuery = useLiveData(membersService.members.searchQuery$);
  const memberListLoading = useLiveData(membersService.members.isLoading$);
  const pageMembers = useLiveData(membersService.members.pageMembers$);

  const isOwner = useLiveData(permissionService.permission.isOwner$);
  const isAdmin = useLiveData(permissionService.permission.isAdmin$);
  const isOwnerOrAdmin = isOwner || isAdmin;
  useEffect(() => {
    permissionService.permission.revalidate();
  }, [permissionService]);

  useEffect(() => {
    membersService.members.revalidate();
  }, [membersService]);

  useEffect(() => {
    if (isOwnerOrAdmin) {
      workspaceShareSettingService.sharePreview.revalidateInviteLink();
    }
  }, [isOwnerOrAdmin, workspaceShareSettingService.sharePreview]);

  const workspaceQuotaService = useService(WorkspaceQuotaService);
  useEffect(() => {
    workspaceQuotaService.quota.revalidate();
  }, [workspaceQuotaService]);
  const isLoading = useLiveData(workspaceQuotaService.quota.isRevalidating$);
  const error = useLiveData(workspaceQuotaService.quota.error$);
  const workspaceQuota = useLiveData(workspaceQuotaService.quota.quota$);
  const subscriptionService = useService(SubscriptionService);
  const plan = useLiveData(
    subscriptionService.subscription.activePlan$.map(s => s?.plan)
  );

  const seatAddonService = useService(SeatAddonService);
  useEffect(() => {
    const permissionsChanged$ = workspace.engine.permissionsChanged$;
    if (!permissionsChanged$) {
      return;
    }

    const permissionsSubscription = permissionsChanged$.subscribe(event => {
      if (
        event.changeType !== 'workspace_member_joined' &&
        event.changeType !== 'workspace_member_role_changed' &&
        event.changeType !== 'workspace_member_revoked' &&
        event.changeType !== 'workspace_member_left'
      ) {
        return;
      }

      membersService.members.revalidate();
      permissionService.permission.revalidate();
      workspaceQuotaService.quota.revalidate();
      seatAddonService.seatAddon.revalidate();
    });

    return () => permissionsSubscription.unsubscribe();
  }, [
    membersService,
    permissionService,
    seatAddonService,
    workspace,
    workspaceQuotaService,
  ]);

  useEffect(() => {
    seatAddonService.seatAddon.revalidate();
  }, [seatAddonService]);
  // sl-92oj: Gate invite button on seat addon data readiness.
  // canPurchaseSeats$ derives from memberQuota$ which starts null and loads
  // asynchronously. If the user clicks invite before it loads, the wrong
  // dialog appears (MemberLimitModal instead of SeatUpsellDialog).
  const isSeatAddonReady = useLiveData(
    seatAddonService.seatAddon.isRevalidating$.map(r => !r)
  );

  const t = useI18n();
  const offlineLabel = useSettingsOfflineIncompleteLabel();

  const [openInvite, setOpenInvite] = useState(false);
  const [openMemberLimit, setOpenMemberLimit] = useState(false);
  const [openSeatUpsell, setOpenSeatUpsell] = useState(false);
  const [pendingEmails, setPendingEmails] = useState<string[]>([]);
  const [pendingPermission, setPendingPermission] = useState(Permission.Editor);
  const [computedSeatsNeeded, setComputedSeatsNeeded] = useState(0);
  const [isMutating, setIsMutating] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const debouncedSearchQuery = useDebouncedValue(localSearchQuery, 300);

  const { openConfirmModal, closeConfirmModal } = useConfirmModal();
  const goToTeamBilling = useCallback(() => {
    onChangeSettingState({
      activeTab: 'workspace:billing',
    });
  }, [onChangeSettingState]);
  const resume = useAsyncCallback(async () => {
    try {
      setIsMutating(true);
      await subscription.resumeSubscription('Team');
      await subscription.waitForRevalidation();
      closeConfirmModal();
      notify.success({
        title: t['com.streamlain.payment.resume.success.title'](),
        message: t['com.streamlain.payment.resume.success.team.message'](),
      });
    } catch (err) {
      const error = UserFriendlyError.fromAny(err);
      notify.error({
        title: error.name,
        message: error.message,
      });
    } finally {
      setIsMutating(false);
    }
  }, [subscription, closeConfirmModal, t]);
  const openInviteModal = useCallback(() => {
    if (isTeam && workspaceSubscription?.canceledAt) {
      openConfirmModal({
        title: t['com.streamlain.payment.member.team.retry-payment.title'](),
        description:
          t[
            `com.streamlain.payment.member.team.disabled-subscription.${isOwner ? 'owner' : 'admin'}.description`
          ](),
        confirmText:
          t[
            isOwner
              ? 'com.streamlain.payment.member.team.disabled-subscription.resume-subscription'
              : 'Got it'
          ](),
        cancelText: t['Cancel'](),
        cancelButtonOptions: {
          style: {
            visibility: isOwner ? 'visible' : 'hidden',
          },
        },
        onConfirm: isOwner ? resume : undefined,
        confirmButtonOptions: {
          variant: 'primary',
          loading: isMutating,
        },
      });

      return;
    }
    setOpenInvite(true);
  }, [
    isMutating,
    isOwner,
    isTeam,
    openConfirmModal,
    resume,
    t,
    workspaceSubscription?.canceledAt,
  ]);

  const onGenerateInviteLink = useCallback(
    async (expireTime: WorkspaceInviteLinkExpireTime) => {
      const { link } = await membersService.generateInviteLink(expireTime);
      workspaceShareSettingService.sharePreview.revalidateInviteLink();
      return link;
    },
    [membersService, workspaceShareSettingService.sharePreview]
  );

  const onRevokeInviteLink = useCallback(async () => {
    const success = await membersService.revokeInviteLink();
    workspaceShareSettingService.sharePreview.revalidateInviteLink();
    return success;
  }, [membersService, workspaceShareSettingService.sharePreview]);

  const onInviteBatchConfirm = useAsyncCallback(
    async ({
      emails,
      permission,
    }: Parameters<InviteTeamMemberModalProps['onConfirm']>[0]) => {
      setIsMutating(true);
      const uniqueEmails = deduplicateEmails(emails);
      // sl-apue: Read quota directly from LiveData (not stale React state closure)
      // to get the freshest value — critical after seat purchase + revalidation.
      const currentQuota = workspaceQuotaService.quota.quota$.value;
      // sl-apue: Use effectiveLimit (base + extra seats) from memberQuota for the
      // over-limit check. workspaceQuota.memberLimit is only the BASE plan limit
      // and doesn't include purchased extra seats.
      const memberQuota = seatAddonService.seatAddon.memberQuota$.value;
      const effectiveLimit =
        memberQuota?.effectiveLimit ?? currentQuota?.memberLimit;
      const currentUsage =
        memberQuota?.currentUsage ?? currentQuota?.memberCount;
      // -1 = unlimited — skip over-limit check entirely
      const isOverLimit =
        effectiveLimit != null &&
        effectiveLimit !== -1 &&
        currentUsage != null &&
        uniqueEmails.length > effectiveLimit - currentUsage;
      if (isOverLimit) {
        // Read directly from LiveData for freshest value (consistent with
        // currentQuota and memberQuota above — avoids stale React state)
        let canPurchase = seatAddonService.seatAddon.canPurchaseSeats$.value;
        // sl-92oj: If canPurchaseSeats is false, re-fetch memberQuota to confirm.
        // For newly created workspaces, the initial fetch may resolve to the
        // wrong plan (stale workspace → owner → subscription chain), returning
        // seatTiers: null even though the user's plan has seat tiers configured.
        // A fresh revalidation gets the correct data. Cost: one extra round-trip,
        // only triggered when over-limit AND initial data says no seat tiers.
        if (!canPurchase) {
          await seatAddonService.seatAddon.waitForRevalidation();
          canPurchase = seatAddonService.seatAddon.canPurchaseSeats$.value;
        }
        if (canPurchase) {
          const availableSlots = effectiveLimit - (currentUsage ?? 0);
          setPendingEmails(uniqueEmails);
          setPendingPermission(permission);
          setComputedSeatsNeeded(
            Math.max(0, uniqueEmails.length - availableSlots)
          );
          setOpenSeatUpsell(true);
        } else {
          setOpenMemberLimit(true);
        }
        setIsMutating(false);
        return;
      }
      const results = await membersService.inviteMembers(uniqueEmails, {
        permission,
      });
      const failedInvites = results.reduce<FailedInvite[]>((acc, result) => {
        if (!result.inviteId) {
          acc.push({
            email: result.email,
            message: inviteFailureMessage(result.error),
          });
        }
        return acc;
      }, []);
      if (results) {
        const failedCount = failedInvites.length;
        const successCount = uniqueEmails.length - failedCount;
        const notification = {
          title:
            failedCount > 0
              ? t['com.streamlain.payment.member.team.invite.notify.title']({
                  successCount: successCount.toString(),
                  failedCount: failedCount.toString(),
                })
              : t['Invitation sent'](),
          message: <NotifyMessage failedInvites={failedInvites} />,
        };
        if (failedCount === 0) {
          notify.success(notification);
        } else {
          notify.warning(notification);
        }
        setOpenInvite(false);
        membersService.members.revalidate();
        workspaceQuotaService.quota.revalidate();
      }
      setIsMutating(false);
    },
    [membersService, seatAddonService, t, workspaceQuotaService]
  );

  const onImportCSV = useAsyncCallback(
    async (file: File) => {
      setIsMutating(true);
      const emails = await parseCSV(file);
      onInviteBatchConfirm({ emails, permission: Permission.Editor });
      setIsMutating(false);
    },
    [onInviteBatchConfirm]
  );

  // sl-284b: seatsNeeded is computed atomically in onInviteBatchConfirm
  // (using LiveData.value for fresh quota data) and stored in state together
  // with pendingEmails. This avoids race conditions where useLiveData values
  // might be null/stale during the render that opens the SeatUpsellDialog.

  const onSeatUpsellSuccess = useAsyncCallback(async () => {
    // sl-apue: Wait for BOTH quota sources to reflect the newly purchased seats.
    // workspaceQuota has base memberLimit; seatAddon has effectiveLimit (base + extra).
    // onInviteBatchConfirm reads effectiveLimit from seatAddon.memberQuota$.value.
    setIsMutating(true);
    await Promise.all([
      workspaceQuotaService.quota.waitForRevalidation(),
      seatAddonService.seatAddon.waitForRevalidation(),
    ]);
    const emails = [...pendingEmails];
    setPendingEmails([]);
    if (emails.length > 0) {
      // Note: onInviteBatchConfirm is useAsyncCallback (returns void, not Promise).
      // It manages its own isMutating state internally.
      onInviteBatchConfirm({ emails, permission: pendingPermission });
    } else {
      setIsMutating(false);
    }
  }, [
    workspaceQuotaService,
    seatAddonService,
    pendingEmails,
    pendingPermission,
    onInviteBatchConfirm,
  ]);

  const handleUpgradeConfirm = useCallback(() => {
    onChangeSettingState({
      activeTab: 'workspace:plans',
      scrollAnchor: 'cloudPricingPlan',
    });
    track.$.settingsPanel.workspace.viewPlans({
      control: 'inviteMember',
    });
  }, [onChangeSettingState]);

  // StreamLain: Hide "Looking to collaborate..." text
  const desc = null;
  // const desc = useMemo(() => {
  //   if (!workspaceQuota) return null;
  //
  //   if (isTeam) {
  //     return <span>{t['com.streamlain.payment.member.team.description']()}</span>;
  //   }
  //   return (
  //     <span>
  //       {t['com.streamlain.payment.member.description2']()}
  //       {hasPaymentFeature && isOwner ? (
  //         <div
  //           className={styles.goUpgradeWrapper}
  //           onClick={handleUpgradeConfirm}
  //         >
  //           <span className={styles.goUpgrade}>
  //             {t['com.streamlain.payment.member.description.choose-plan']()}
  //           </span>
  //         </div>
  //       ) : null}
  //     </span>
  //   );
  // }, [
  //   handleUpgradeConfirm,
  //   hasPaymentFeature,
  //   isOwner,
  //   isTeam,
  //   t,
  //   workspaceQuota,
  // ]);

  const title = useMemo(() => {
    // StreamLain: Hide member limit display
    return workspaceQuota?.memberCount === undefined
      ? t['Members']()
      : `${t['Members']()} (${workspaceQuota.memberCount})`;
  }, [t, workspaceQuota?.memberCount]);

  const showOfflineNotice =
    !backendAvailable || (error !== null && isBackendUnavailableError(error));

  useEffect(() => {
    if (debouncedSearchQuery !== searchQuery) {
      membersService.members.setSearchQuery(debouncedSearchQuery);
      membersService.members.setPageNum(0);
      membersService.members.revalidate();
    }
  }, [debouncedSearchQuery, searchQuery, membersService]);

  const handlePageChange = useCallback(
    (page: number) => {
      membersService.members.setPageNum(page);
      membersService.members.revalidate();
    },
    [membersService]
  );

  if (workspaceQuota === null) {
    if (isLoading && !showOfflineNotice) {
      return <MembersPanelFallback />;
    }

    return (
      <SettingsEntityPage
        title={t['Members']()}
        subtitle=""
        headerAction={<SettingsOfflineHeaderAction show={showOfflineNotice} />}
      >
        <SettingsEntityMessage>
          {showOfflineNotice
            ? offlineLabel
            : error
              ? UserFriendlyError.fromAny(error).message
              : 'Failed to load members'}
        </SettingsEntityMessage>
      </SettingsEntityPage>
    );
  }

  return (
    <>
      <SettingsSearchableEntityPage
        title={title}
        subtitle={desc ?? ''}
        headerAction={<SettingsOfflineHeaderAction show={showOfflineNotice} />}
        searchValue={localSearchQuery}
        onSearchChange={setLocalSearchQuery}
        searchPlaceholder={
          t['com.streamlain.settings.workspace.members.search']?.() ??
          'Search members...'
        }
        action={
          isOwnerOrAdmin ? (
            <Button
              data-testid="settings-members-invite-button"
              onClick={openInviteModal}
              disabled={!isSeatAddonReady || showOfflineNotice}
            >
              {t['Invite Members']()}
            </Button>
          ) : undefined
        }
        banner={<SeatQuotaBanner onChangeSettingState={onChangeSettingState} />}
        loading={!!memberListLoading && !pageMembers && !showOfflineNotice}
        hasItems={(pageMembers?.length ?? 0) > 0}
        loadingContent={<MemberListFallback />}
        emptyContent={
          <SettingsEntityMessage>
            {showOfflineNotice
              ? offlineLabel
              : (t[
                  'com.streamlain.settings.workspace.members.no-results'
                ]?.() ?? 'No members found')}
          </SettingsEntityMessage>
        }
        pagination={{
          page: pageNum + 1,
          pageCount:
            Math.ceil((memberCount ?? 0) / membersService.members.PAGE_SIZE) ||
            1,
          pageSize: membersService.members.PAGE_SIZE,
          totalItems: memberCount ?? 0,
          onPageChange: p => handlePageChange(p - 1),
          previousLabel: t['Previous']?.() ?? 'Previous',
          nextLabel: t['Next']?.() ?? 'Next',
        }}
      >
        <MemberList
          members={pageMembers}
          isOwner={!!isOwner}
          isAdmin={!!isAdmin}
          goToTeamBilling={goToTeamBilling}
        />
      </SettingsSearchableEntityPage>

      <MemberLimitModal
        isFreePlan={!plan}
        open={openMemberLimit}
        plan={workspaceQuota.humanReadable.name ?? ''}
        quota={workspaceQuota.humanReadable.memberLimit ?? ''}
        setOpen={setOpenMemberLimit}
        onConfirm={handleUpgradeConfirm}
      />
      <SeatUpsellDialog
        open={openSeatUpsell}
        setOpen={setOpenSeatUpsell}
        seatsNeeded={computedSeatsNeeded}
        onSuccess={onSeatUpsellSuccess}
        onUpgrade={handleUpgradeConfirm}
      />
      <InviteTeamMemberModal
        open={openInvite}
        setOpen={setOpenInvite}
        onConfirm={onInviteBatchConfirm}
        isMutating={isMutating}
        copyTextToClipboard={copyTextToClipboard}
        onGenerateInviteLink={onGenerateInviteLink}
        onRevokeInviteLink={onRevokeInviteLink}
        importCSV={<ImportCSV onImport={onImportCSV} />}
        invitationLink={inviteLink}
      />
    </>
  );
};

const NotifyMessage = ({
  failedInvites,
}: {
  failedInvites: FailedInvite[];
}) => {
  const t = useI18n();

  if (failedInvites.length === 0) {
    return t['Invitation sent hint']();
  }

  return (
    <div>
      {t['com.streamlain.payment.member.team.invite.notify.fail-message']()}
      {failedInvites.map((invite, index) => (
        <div key={`${index}:${invite.email}`}>
          {invite.message ? `${invite.email}: ${invite.message}` : invite.email}
        </div>
      ))}
    </div>
  );
};

export const MembersPanelFallback = () => {
  const t = useI18n();

  return (
    <SettingsEntityPage title={t['Members']()} subtitle="">
      <MemberListFallback />
    </SettingsEntityPage>
  );
};

const ImportCSV = ({ onImport }: { onImport: (file: File) => void }) => {
  const t = useI18n();

  return (
    <Upload accept="text/csv" fileChange={onImport}>
      <Button
        className={styles.importButton}
        prefix={<ExportIcon />}
        variant="secondary"
      >
        {t['com.streamlain.payment.member.team.invite.import-csv']()}
      </Button>
    </Upload>
  );
};

function deduplicateEmails(emails: string[]): string[] {
  const seenEmails = new Set<string>();
  return emails.filter(email => {
    const lowerCaseEmail = email.trim().toLowerCase();
    if (seenEmails.has(lowerCaseEmail)) {
      return false;
    } else {
      seenEmails.add(lowerCaseEmail);
      return true;
    }
  });
}
